import { Document, Paragraph, TextRun, Packer, HeadingLevel, AlignmentType } from 'docx'

export const generateDocument = async (documentData) => {
  const {
    documentType,
    customer,
    contact,
    selectedProducts,
    products,
    documentDate,
    pricing,
    documentId
  } = documentData

  // Create document content
  const documentChildren = []

  // Header
  documentChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: documentType === 'price-info' ? 'PRICE INFORMATION' : 'SERVICE CONTRACT',
          bold: true,
          size: 32
        })
      ],
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: `Document ID: ${documentId}`,
          size: 20
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: `Date: ${formatDate(documentDate)}`,
          size: 20
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    })
  )

  // Customer Information
  documentChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'CUSTOMER INFORMATION',
          bold: true,
          size: 24
        })
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: 'Company: ',
          bold: true
        }),
        new TextRun(customer.Name)
      ],
      spacing: { after: 120 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: 'Contact: ',
          bold: true
        }),
        new TextRun(contact ? contact.Name : 'To Whom It May Concern')
      ],
      spacing: { after: 120 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: 'Address: ',
          bold: true
        }),
        new TextRun(`${customer.BillingStreet || ''}, ${customer.BillingCity || ''} ${customer.BillingPostalCode || ''}`)
      ],
      spacing: { after: 400 }
    })
  )

  // Products Section
  const mainProducts = selectedProducts.filter(id => id.startsWith('prod_'))
  const externalServices = selectedProducts.filter(id => id.startsWith('ext_'))

  if (mainProducts.length > 0) {
    documentChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'SELECTED PRODUCTS',
            bold: true,
            size: 24
          })
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      })
    )

    mainProducts.forEach(productId => {
      const product = products.find(p => p.id === productId)
      if (product) {
        documentChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${product.name} - ${product.variant}`,
                bold: true,
                size: 20
              })
            ],
            spacing: { before: 200, after: 120 }
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: 'Monthly Price: ',
                bold: true
              }),
              new TextRun(`€${product.price}`)
            ]
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: 'Features: ',
                bold: true
              }),
              new TextRun(product.description)
            ]
          })
        )

        if (documentType === 'contract' && product.contractDetails) {
          documentChildren.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Terms & Conditions: ',
                  bold: true
                }),
                new TextRun(product.contractDetails)
              ],
              spacing: { before: 120, after: 200 }
            })
          )
        }
      }
    })
  }

  // External Services
  if (externalServices.length > 0) {
    documentChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'ADDITIONAL SERVICES',
            bold: true,
            size: 24
          })
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      })
    )

    externalServices.forEach(serviceId => {
      const service = products.find(p => p.id === serviceId)
      if (service) {
        documentChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: service.name,
                bold: true
              }),
              new TextRun(` - €${service.price}/month`)
            ]
          }),

          new Paragraph({
            children: [new TextRun(service.description)],
            spacing: { after: 120 }
          })
        )

        if (documentType === 'contract' && service.contractDetails) {
          documentChildren.push(
            new Paragraph({
              children: [new TextRun(service.contractDetails)],
              spacing: { after: 200 }
            })
          )
        }
      }
    })
  }

  // Bundle Discount
  if (pricing.hasDiscount) {
    documentChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: '🎉 BUNDLE DISCOUNT APPLIED',
            bold: true,
            color: 'FF0000',
            size: 24
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 120 }
      }),

      new Paragraph({
        children: [
          new TextRun('Special pricing applies when multiple products are selected together. You save 10% on your total investment!')
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      })
    )
  }

  // Pricing Summary
  documentChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'PRICING SUMMARY',
          bold: true,
          size: 24
        })
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    })
  )

  // Individual product prices
  selectedProducts.forEach(productId => {
    const product = products.find(p => p.id === productId)
    if (product) {
      documentChildren.push(
        new Paragraph({
          children: [
            new TextRun(`${product.name} ${product.variant ? `(${product.variant})` : ''}: €${product.price}/month`)
          ]
        })
      )
    }
  })

  if (pricing.hasDiscount) {
    documentChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Subtotal: €${pricing.originalTotal}/month`,
            bold: true
          })
        ],
        spacing: { before: 200 }
      }),

      new Paragraph({
        children: [
          new TextRun({
            text: `Bundle Discount (10%): -€${(pricing.originalTotal - pricing.total).toFixed(2)}`,
            color: '008000'
          })
        ]
      })
    )
  }

  documentChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `TOTAL: €${pricing.total.toFixed(2)}/month`,
          bold: true,
          size: 24
        })
      ],
      spacing: { before: 200 }
    })
  )

  // Create document
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1440,
            right: 1440,
            bottom: 1440,
            left: 1440,
          }
        }
      },
      children: documentChildren
    }]
  })

  // Generate file (use toBlob for browser compatibility)
  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${documentType}-${customer.Name.replace(/\s+/g, '-')}-${Date.now()}.docx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}